import { z } from 'zod';
import { AppLink } from './ai.js';
import { StageId } from './awards.js';
import { PlainDate } from './common.js';

/**
 * Ask the data (RPT5), and from APP-191 the Executive chat's planner (APP-171's core): the query
 * the model may produce, the answer the server works out from it, and the calls around them. The
 * model only ever produces a query from these closed lists; the server runs it in the asker's own
 * scope and computes every figure (APP-048).
 */

// ---- Ask the data (RPT5) -------------------------------------------------------------------

export const AskDataReport = z.enum(['coverage', 'totals', 'repeat', 'pace']);
export type AskDataReport = z.infer<typeof AskDataReport>;
export const AskDataGroupBy = z.enum([
  'sector',
  'award_type',
  'pool',
  'program',
  'month',
  'quarter',
  'none',
]);
export type AskDataGroupBy = z.infer<typeof AskDataGroupBy>;
export const AskDataMeasure = z.enum([
  'share_recognized',
  'people_recognized',
  'awards',
  'value',
  'cash',
  'options',
  'per_100_people',
]);
export type AskDataMeasure = z.infer<typeof AskDataMeasure>;
export const AskDataAwardType = z.enum(['SDB', 'EDB', 'PAA', 'TAA', 'SPOT']);

export const AskDataPeriodV1 = z.discriminatedUnion('kind', [
  /** The last N months ending today (recognition counts over a rolling 12 months by default). */
  z.object({ kind: z.literal('rolling_months'), months: z.number().int().min(1).max(36) }).strict(),
  /** A fiscal year, to date when it's the current one. */
  z.object({ kind: z.literal('fiscal_year'), year: z.number().int().min(2000).max(2100) }).strict(),
  z.object({ kind: z.literal('range'), from: PlainDate, to: PlainDate }).strict(),
]);
export type AskDataPeriodV1 = z.infer<typeof AskDataPeriodV1>;

/**
 * The only thing the model produces for Ask the data: a query over allow-listed reports, filters,
 * groupings and measures. The server validates it and runs it with the asker's own scope; it
 * never runs SQL from the model. A pin stores exactly this, so it re-runs without AI.
 */
export const AskDataQueryV1 = z
  .object({
    version: z.literal(1),
    report: AskDataReport,
    period: AskDataPeriodV1,
    filters: z
      .object({
        sectorIds: z.array(z.string().min(1).max(60)).max(10),
        awardTypes: z.array(AskDataAwardType).max(5),
        poolIds: z.array(z.string().min(1).max(60)).max(10),
        excludePoolIds: z.array(z.string().min(1).max(60)).max(10),
        /** Only lines that pay cash ("cash awards only"). */
        cashOnly: z.boolean(),
      })
      .strict(),
    /** Totals: rows. Coverage is always by sector; repeat by person; pace by month. */
    groupBy: AskDataGroupBy,
    /** Totals: an optional second dimension shown side by side (sectors per quarter). */
    seriesBy: z.enum(['sector', 'award_type', 'none']),
    measure: AskDataMeasure,
    /** Repeat: at least this many awards in the period. */
    minAwards: z.number().int().min(1).max(50),
    sort: z.enum(['asc', 'desc']),
    limit: z.number().int().min(1).max(100),
    /**
     * What `limit` asks for (APP-217, AIE-071): `all`, every group and the whole period (limit
     * unused); `top`, only the first `limit` rows in the sort, as "the top 10 recipients" asks;
     * `latest`, a month or quarter series of the latest `limit` months or quarters, which are its
     * period. Left out (a question saved before), a repeat list keeps its limit as a top and
     * everything else is `all`.
     */
    rows: z.enum(['all', 'top', 'latest']).optional(),
  })
  .strict();
export type AskDataQueryV1 = z.infer<typeof AskDataQueryV1>;
export type AskDataRows = NonNullable<AskDataQueryV1['rows']>;

export const AskDataChartV1 = z.object({
  kind: z.enum(['bar', 'column', 'line']),
  unit: z.enum(['percent', 'count', 'cents', 'per_100']),
  categories: z.array(z.string()),
  series: z.array(z.object({ key: z.string(), label: z.string(), values: z.array(z.number()) })),
  /** A reference line (the company average). */
  reference: z.object({ label: z.string(), value: z.number() }).nullable(),
  /** Category indexes drawn highlighted (behind the company average), with the words why. */
  highlighted: z.array(z.number().int()),
  highlightLabel: z.string().nullable(),
  /** The chart in words, for screen readers. */
  description: z.string(),
});
export type AskDataChartV1 = z.infer<typeof AskDataChartV1>;

export const AskDataCell = z.union([z.string(), z.number(), z.null()]);
export const AskDataTableV1 = z.object({
  columns: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      format: z.enum(['text', 'count', 'cents', 'percent', 'per_100']),
    }),
  ),
  rows: z.array(z.record(z.string(), AskDataCell)),
  total: z.record(z.string(), AskDataCell).nullable(),
});
export type AskDataTableV1 = z.infer<typeof AskDataTableV1>;

// ---- Version 2 (APP-191 slice 1, APP-171's planner) -------------------------------------------
//
// A measure, at most two dimensions, a period, filters, a comparison and an optional named
// analysis. Version 1's query stays above so saved questions, pins and stored payloads still
// parse; the server reads them through `readQuery`, which upgrades them. Its chart and table
// shapes stay too: the ported families (coverage, repeat, pace) build in them and reshape into
// version 2. APP-255 removed version 1's chips and result, which nothing builds any more.

/** What a figure counts or sums. */
export const AskMeasure = z.enum([
  'value',
  'cash',
  'options',
  'awards',
  'recipients',
  'people_recognized',
  'share_recognized',
  'per_100_people',
  'average_value',
  'median_value',
  'budget',
  'used',
  'in_progress',
  'paid',
  'left',
  'used_share',
  'nominations',
  'approved_share',
  'days_waiting',
  'days_to_paid',
]);
export type AskMeasure = z.infer<typeof AskMeasure>;

/** What a figure is broken down by. Two of them at most: `groupBy`, then `splitBy`. */
export const AskDimension = z.enum([
  'sector',
  'award_type',
  'pool',
  'program',
  'stage',
  'fiscal_year',
  'quarter',
  'month',
  'person',
  'payout',
  'election',
  'spot_status',
]);
export type AskDimension = z.infer<typeof AskDimension>;
export const AskGroupBy = z.enum([...AskDimension.options, 'none']);
export type AskGroupBy = z.infer<typeof AskGroupBy>;

/** A named analysis with its own shape; `none` is a plain measure by its dimensions. */
export const AskAnalysis = z.enum([
  'none',
  'coverage',
  'repeat',
  'pace',
  'burn',
  'distribution',
  'concentration',
  'ledger',
  'stages',
  // Task 1.17: one person's award history, line by line, each checked as an award record.
  'history',
  // APP-251 (AIE-077): the asker's own awards, exactly as My rewards shows them to them.
  'mine',
]);
export type AskAnalysis = z.infer<typeof AskAnalysis>;

export const AskCompare = z.enum(['none', 'previous_year', 'previous_period', 'budget']);
export type AskCompare = z.infer<typeof AskCompare>;

/** Every picture the chat can draw. The server picks one from the answer's shape (D3). */
export const ChartForm = z.enum([
  'tiles',
  'bars',
  'columns',
  'lines',
  'stacked',
  'grouped',
  'percent_bar',
  'donut',
  'bullets',
  'burn_up',
  'waterfall',
  'heat_map',
  'histogram',
  'concentration',
  'small_multiples',
  'dumbbell',
  'stage_bars',
  'table_bars',
  'flow',
]);
export type ChartForm = z.infer<typeof ChartForm>;

/** How the answer is shown: the server's choice, the table, or a picture asked for by name. */
export const AskView = z.enum(['auto', 'table', ...ChartForm.options]);
export type AskView = z.infer<typeof AskView>;

/** The most fiscal years one answer spans (`fiscal_years`): six, oldest to newest. */
export const ASK_MAX_FISCAL_YEARS = 6;

/**
 * APP-266 (APP-191 D7, D14): the most rows an export holds: every row the answer counts, up to
 * 1,000, its total and `rowCount` counting them all. The page says when the export has the first
 * 1,000 of more. (The answer itself shows its top N, or the first 100 of a longer list.)
 */
export const ASK_DATA_TABLE_ROWS = 1000;

/**
 * APP-266 (SENT-192): the most cells a table with every row holds, as a split adds a column for each
 * of its groups. It takes every month of six years for 1,000 people; 300 programs leave room for
 * 331 rows. Measured in the decision record.
 */
export const ASK_DATA_TABLE_CELLS = 100_000;

/**
 * How many rows a table with every row holds for an answer of this many columns, so a file and the
 * page's words agree: at most `ASK_DATA_TABLE_ROWS`, within the cells (the mark's column counted),
 * and always a row.
 */
export function askDataTableRows(columns: number, cells: number = ASK_DATA_TABLE_CELLS): number {
  return Math.max(1, Math.min(ASK_DATA_TABLE_ROWS, Math.floor(cells / (columns + 1))));
}

/**
 * APP-266: the column a table with every row marks the answer's own rows in ("Top 10"). It's in a
 * file and in the read behind the page's "Show all", never in an answer.
 */
export const ASK_DATA_MARK_KEY = 'in_answer';

const FiscalYearNumber = z.number().int().min(2000).max(2100);
export const AskDataPeriod = z
  .discriminatedUnion('kind', [
    /** The last N months: whole calendar months, from the 1st of the month N − 1 back (APP-199). */
    z
      .object({ kind: z.literal('rolling_months'), months: z.number().int().min(1).max(36) })
      .strict(),
    /** One fiscal year, the whole year as All awards counts it. */
    z.object({ kind: z.literal('fiscal_year'), year: FiscalYearNumber }).strict(),
    /** Several fiscal years, oldest to newest ("the last three fiscal years"). */
    z
      .object({ kind: z.literal('fiscal_years'), from: FiscalYearNumber, to: FiscalYearNumber })
      .strict(),
    z.object({ kind: z.literal('range'), from: PlainDate, to: PlainDate }).strict(),
  ])
  .refine(
    (p) => p.kind !== 'fiscal_years' || (p.to >= p.from && p.to - p.from < ASK_MAX_FISCAL_YEARS),
    { message: `At most ${String(ASK_MAX_FISCAL_YEARS)} fiscal years, oldest first` },
  );
export type AskDataPeriod = z.infer<typeof AskDataPeriod>;

const Id = z.string().min(1).max(60);

/**
 * Version 2 of the query (APP-191 D1). The model emits it inside `answer_plan` from closed lists
 * and the vocabulary's ids; the server validates it against the asker's own vocabulary and runs
 * it in their scope. `rows` is APP-217's: a limit cuts only a top N the question asked for, or
 * makes a month or quarter series the latest N.
 */
export const AskDataQuery = z
  .object({
    version: z.literal(2),
    measure: AskMeasure,
    groupBy: AskGroupBy,
    splitBy: AskGroupBy,
    period: AskDataPeriod,
    compare: AskCompare,
    analysis: AskAnalysis,
    view: AskView,
    filters: z
      .object({
        sectorIds: z.array(Id).max(10),
        awardTypes: z.array(AskDataAwardType).max(5),
        poolIds: z.array(Id).max(10),
        excludePoolIds: z.array(Id).max(10),
        programIds: z.array(Id).max(20),
        stages: z.array(StageId).max(6),
        /** Only lines that pay cash ("cash awards only"). */
        cashOnly: z.boolean(),
        /**
         * Task 1.17: whose award history. Never from the model: the server sets it from a name it
         * resolved in the asker's own people scope, and checks it again on every run. A query
         * stored without it is about no one person.
         */
        personId: z.uuid().nullable().optional(),
      })
      .strict(),
    sort: z.enum(['asc', 'desc']),
    limit: z.number().int().min(1).max(100),
    /** Repeat: at least this many awards in the period. */
    minAwards: z.number().int().min(1).max(50),
    rows: z.enum(['all', 'top', 'latest']),
  })
  .strict();
export type AskDataQuery = z.infer<typeof AskDataQuery>;

export const AskDataChip = z.object({
  key: z.enum([
    'period',
    'sector',
    'award_type',
    'pool',
    'measure',
    'cash',
    'group_by',
    'split_by',
    'compare',
    'program',
    'stage',
  ]),
  label: z.string(),
  value: z.string(),
  /** False for chips the analysis fixes (recognition is always the last 12 months). */
  editable: z.boolean(),
  /** Changed from the answer's first query. */
  changed: z.boolean(),
  options: z.array(z.object({ label: z.string(), selected: z.boolean(), query: AskDataQuery })),
});
export type AskDataChip = z.infer<typeof AskDataChip>;

/** What a panel's numbers are in. Options are a count of options, never money. */
export const AskUnit = z.enum(['percent', 'count', 'cents', 'per_100', 'options', 'days']);
export type AskUnit = z.infer<typeof AskUnit>;

/** A series' colour: one of the six palette slots (an award type keeps its own), or the single. */
export const AskSeriesSlot = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal('single'),
]);
export type AskSeriesSlot = z.infer<typeof AskSeriesSlot>;

export const AskDataChart = z.object({
  /** The picture the server chose for the answer's shape and the view asked for. */
  form: ChartForm,
  /** Other pictures that fit the same numbers, best first ("View as"). */
  alternatives: z.array(ChartForm),
  /** Pictures that don't fit, with the reason in words ("the figures aren't parts of one whole"). */
  notOffered: z.array(z.object({ form: ChartForm, reason: z.string() })),
  /** When the view asked for doesn't fit: what was drawn instead, and why. */
  viewNote: z.string().nullable(),
  /** One panel per unit: cash and options are two panels, never two axes on one. */
  panels: z.array(
    z.object({
      unit: AskUnit,
      label: z.string(),
      series: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          slot: AskSeriesSlot,
          /** `target` is what a bullet measures against (a budget); every other series is a measure. */
          role: z.enum(['measure', 'target']),
          /** One per category; null where a group is too small for a rate, or not reached yet. */
          values: z.array(z.number().nullable()),
        }),
      ),
    }),
  ),
  categories: z.array(z.string()),
  /** Time runs left to right; ordered categories keep their order (stages); value is ranked. */
  categoryOrder: z.enum(['time', 'ordered', 'value']),
  references: z.array(
    z.object({
      kind: z.enum(['average', 'budget', 'last_year']),
      label: z.string(),
      value: z.number(),
    }),
  ),
  /** Category indexes drawn highlighted, with the words why. */
  highlighted: z.array(z.number().int()),
  highlightLabel: z.string().nullable(),
  /** A projection's band, from a category index on: always an Estimate. */
  band: z
    .object({
      from: z.number().int().nonnegative(),
      low: z.array(z.number()),
      high: z.array(z.number()),
      label: z.literal('Estimate'),
    })
    .nullable(),
  /** Category indexes over budget, drawn with a pattern and the word "Over". */
  overBudget: z.array(z.number().int()),
  /**
   * "Top 30 of 120": the chart shows the first `shown` of its `of` categories, and `label` says
   * which, in the answer's own words ("Top 3 of the 4 sectors and Executive Discretionary with
   * awards"). The counts are rows, the no-sector row included; only the words name sectors, and
   * they never count Executive Discretionary as one (APP-251 AIE-082, APP-255).
   */
  topOf: z.object({ shown: z.number().int(), of: z.number().int(), label: z.string() }).nullable(),
  /** The chart in words, for screen readers. */
  description: z.string(),
});
export type AskDataChart = z.infer<typeof AskDataChart>;

export const AskFormat = z.enum([
  'text',
  'count',
  'cents',
  'percent',
  'per_100',
  'options',
  'days',
]);
export type AskFormat = z.infer<typeof AskFormat>;

export const AskDataTable = z.object({
  columns: z.array(z.object({ key: z.string(), label: z.string(), format: AskFormat })),
  rows: z.array(z.record(z.string(), AskDataCell)),
  total: z.record(z.string(), AskDataCell).nullable(),
  /** The last column is each row's total across the split. */
  totalColumn: z.boolean(),
  /**
   * Every row the answer has; the table may show fewer: a top N the question asked for, or the
   * first 100 of a longer list. An export holds them all, up to `ASK_DATA_TABLE_ROWS` (APP-266).
   */
  rowCount: z.number().int().nonnegative(),
});
export type AskDataTable = z.infer<typeof AskDataTable>;

/** One headline figure, drawn as a tile ("Value · $1,204,000 · Estimate"). */
export const AskFigure = z.object({
  label: z.string(),
  value: z.number(),
  format: AskFormat.exclude(['text']),
  note: z.string().nullable(),
  /** It includes option value, or it's a projection. */
  estimate: z.boolean(),
});
export type AskFigure = z.infer<typeof AskFigure>;

export const AskDataResult = z.object({
  query: AskDataQuery,
  title: z.string(),
  subtitle: z.string(),
  /** Whose figures these are and when: "Every sector and pool · FY 2026 to Oct 26". */
  scopeLine: z.string(),
  /** The findings, worked out from the numbers by the server, in plain sentences (D10 A). */
  summary: z.array(z.string()),
  figures: z.array(AskFigure),
  /** What the figures leave out or hold back: zero rows dropped, small groups, imported gaps. */
  notes: z.array(z.string()),
  /** Any figure includes option value or a projection. */
  estimate: z.boolean(),
  chips: z.array(AskDataChip),
  chart: AskDataChart.nullable(),
  table: AskDataTable,
  how: z.object({
    lines: z.array(z.string()),
    scope: z.string(),
    dataAsOf: z.string(),
    rowCount: z.number().int().nonnegative(),
    queryText: z.array(z.string()),
  }),
  fileStem: z.string(),
});
export type AskDataResult = z.infer<typeof AskDataResult>;

// ---- Asking, answers and saved questions ----------------------------------------------------

export const AskDataAsk = z
  .object({
    question: z.string().trim().min(1).max(1000),
    /** A follow-up refines the conversation's last query ("Now only cash"). */
    conversationId: z.uuid().nullable(),
    /**
     * APP-258 (slice 3, D9): a new conversation started inside one of the asker's own projects.
     * It's kept in that project, and its first question starts from the project's default scope.
     * Ignored on a follow-up (the conversation's project stands). Someone else's project is 404.
     */
    projectId: z.uuid().nullable().optional(),
  })
  .strict();
export type AskDataAsk = z.infer<typeof AskDataAsk>;

export const AskDataRefusal = z.object({
  kind: z.enum([
    'outside_myrewards',
    'read_only',
    'not_understood',
    // APP-191: a name the asker's MyRewards doesn't hold (or holds outside their scope: the same
    // reply), a year with no awards, an answer too big for one reply, an answer their Insights
    // wouldn't show, a question that could mean several things, and "what's waiting for me".
    'unknown_name',
    'no_such_year',
    'too_much',
    'not_in_your_insights',
    'ambiguous',
    'waiting_on_me',
  ]),
  title: z.string(),
  text: z.string(),
  link: AppLink.nullable(),
  /** For `ambiguous`: what it might mean, each the server's own query, run without AI. */
  choices: z.array(z.object({ label: z.string(), query: AskDataQuery })).optional(),
});
export type AskDataRefusal = z.infer<typeof AskDataRefusal>;

/**
 * APP-263 (APP-191 D10 C): the Automated summary of an answer, asked for in words ("summarize
 * this year's awards") while its switch is on. `written`: one paragraph the model wrote from the
 * answer's totals, every figure in it checked against them. `left_out`: it wasn't shown, and why
 * (a figure in it couldn't be checked, or it couldn't be written just now); the table and findings
 * stand alone. Absent when no summary was asked for or its switch is off.
 */
export const AskAutomatedSummary = z.discriminatedUnion('status', [
  z.object({ status: z.literal('written'), paragraph: z.string() }),
  z.object({ status: z.literal('left_out'), note: z.string() }),
]);
export type AskAutomatedSummary = z.infer<typeof AskAutomatedSummary>;

export const AskDataAnswer = z.object({
  conversationId: z.string(),
  messageId: z.string(),
  question: z.string(),
  /**
   * The reply bubble: worked out from the result, or the refusal. A nearest answer, or a follow-up
   * that changed nothing, starts with the server's note saying so (APP-211).
   */
  reply: z.array(z.string()),
  /** What changed from the conversation's last query, for a follow-up. */
  changes: z.array(z.object({ label: z.string(), from: z.string(), to: z.string() })),
  result: AskDataResult.nullable(),
  refusal: AskDataRefusal.nullable(),
  /**
   * The query was worked out by AI from the question. False when the server answered alone: a
   * view asked for in words ("show that as a donut", APP-257), which no model saw.
   */
  aiGenerated: z.boolean(),
  /**
   * APP-257 (D6): true when the words asked only for another picture of the conversation's last
   * answer, re-drawn here with no AI and nothing counted. Absent otherwise.
   */
  viaWords: z.literal(true).optional(),
  /** APP-263: the Automated summary, when one was asked for with its switch on. */
  automatedSummary: AskAutomatedSummary.nullable().optional(),
  /**
   * APP-263 (D13): opened from an answer the assistant gave in Teams or by email, worked out again
   * here with no AI. Absent otherwise.
   */
  openedFrom: z.enum(['teams', 'email']).optional(),
});
export type AskDataAnswer = z.infer<typeof AskDataAnswer>;

/**
 * APP-263 (APP-191 D13): `POST /v1/insights/ask/open`, an answer the assistant gave in Teams or
 * by email ("Open this as a chart in MyRewards", `/insights/ask?answer=<uuid>`), opened as a new
 * Ask the data conversation: its query worked out again in the person's scope now, with no AI.
 * Only the person's own answer opens; anyone else's reads as one that doesn't exist (404).
 */
export const AskOpenAnswer = z.object({ answer: z.uuid() }).strict();
export type AskOpenAnswer = z.infer<typeof AskOpenAnswer>;

/**
 * A query sent back to run, pin or export: version 2, or a version 1 query from a page loaded
 * before APP-191 (the server reads either through `readQuery`).
 */
export const AskDataSentQuery = z.union([AskDataQuery, AskDataQueryV1]);
export type AskDataSentQuery = z.infer<typeof AskDataSentQuery>;

/** Re-run a query without AI: a chip change, or opening a saved question. */
export const AskDataRun = z.object({ query: AskDataSentQuery }).strict();
export type AskDataRun = z.infer<typeof AskDataRun>;

/**
 * APP-266 (D14): every row of an answer for the page's table, up to `ASK_DATA_TABLE_ROWS`. The
 * server re-runs the query in the asker's scope, as `AskDataRun` and an export do, so the body holds
 * the query alone (strict: a body with figures, a message or a conversation is refused), and the
 * response is an `AskDataTable`, never a whole answer. It's kept nowhere and reaches no model.
 */
export const AskDataAllRows = z.object({ query: AskDataSentQuery }).strict();
export type AskDataAllRows = z.infer<typeof AskDataAllRows>;

/** APP-257 (D7): what an answer exports to, an Excel workbook or CSV. */
export const AskDataExportFormat = z.enum(['xlsx', 'csv']);
export type AskDataExportFormat = z.infer<typeof AskDataExportFormat>;

/**
 * APP-257 (D7): export an answer. The server re-runs it from its query in the asker's scope and
 * writes the file itself, so the body holds the query and the format only (strict: a body that
 * carries figures is refused). Left out, the format is CSV, as the export was before.
 */
export const AskDataExport = z
  .object({ query: AskDataSentQuery, format: AskDataExportFormat.default('csv') })
  .strict();
export type AskDataExport = z.infer<typeof AskDataExport>;

export const AskDataSaved = z.object({
  id: z.string(),
  title: z.string(),
  /** The query in a line ("2026 vs 2025 · all discretionary pools · Awarded ($)"). */
  description: z.string(),
  query: AskDataQuery,
  savedAt: z.string(),
  /**
   * A filter names something no longer in the asker's MyRewards (renamed away, archived or out of
   * their scope): the question opens to a designed refusal, never to a wider answer.
   */
  stale: z.enum(['sector', 'pool', 'program']).nullable(),
  /** APP-258 (D9): the project whose board it's on; null for one saved before projects. */
  projectId: z.string().nullable(),
  /** APP-258: the conversation it was pinned from, while it's kept; null otherwise. */
  conversationId: z.string().nullable(),
});
export type AskDataSaved = z.infer<typeof AskDataSaved>;

export const AskDataSavedList = z.object({ saved: z.array(AskDataSaved) });
export type AskDataSavedList = z.infer<typeof AskDataSavedList>;

export const AskDataPin = z
  .object({
    title: z.string().trim().min(1).max(200),
    query: AskDataSentQuery,
    /**
     * APP-258 (D9): the asker's own project whose board it goes on. Left out or null, it goes on
     * "My reports" (made then if the person has none). Someone else's project is 404.
     */
    projectId: z.uuid().nullable().optional(),
    /**
     * APP-258: the answer it was pinned from (an assistant message in the asker's own Ask the data
     * conversation), so the card can open the conversation it came from. Someone else's is 404.
     */
    messageId: z.uuid().nullable().optional(),
  })
  .strict();
export type AskDataPin = z.infer<typeof AskDataPin>;

export const AskDataStarters = z.object({
  /** Ask the data takes new questions now (master, feature, key, audience and role). */
  available: z.boolean(),
  starters: z.array(z.object({ question: z.string(), hint: z.string(), query: AskDataQuery })),
});
export type AskDataStarters = z.infer<typeof AskDataStarters>;

// ---- Views and images (APP-191 slice 2, APP-256: the web half) -------------------------------

/**
 * The view a person switched an answer to (D6): the picture they chose, or the table. It's
 * remembered on the answer's message, so reopening the conversation shows it (slice 3), and it
 * changes no figure: the same figures, drawn another way, with no AI and nothing counted.
 */
export const AskDataViewChange = z.object({ view: AskView }).strict();
export type AskDataViewChange = z.infer<typeof AskDataViewChange>;

export const AskDataViewSaved = z.object({ messageId: z.string(), view: AskView });
export type AskDataViewSaved = z.infer<typeof AskDataViewSaved>;

/**
 * A chart image downloaded or copied (D7): recorded with the query it was drawn from, the view,
 * and what was made, so every image of financial data has its audit entry. The image itself is
 * drawn in the browser from the server's figures and never sent.
 */
export const AskDataImageKind = z.enum(['png', 'svg', 'copy']);
export type AskDataImageKind = z.infer<typeof AskDataImageKind>;
export const AskDataImageAudit = z
  .object({ query: AskDataSentQuery, view: AskView, kind: AskDataImageKind })
  .strict();
export type AskDataImageAudit = z.infer<typeof AskDataImageAudit>;
export const AskDataImageRecorded = z.object({ recorded: z.literal(true) });
export type AskDataImageRecorded = z.infer<typeof AskDataImageRecorded>;

// ---- Keeping answers (APP-191 slice 3, APP-258: the server half) -----------------------------
//
// D8 and D9: a person's own Ask the data conversations, kept on the server with each answer's
// snapshot, and projects with a board of pinned answers. Owner-only: another person's id reads
// exactly as one that doesn't exist (404). Every figure shown again is either the snapshot taken
// under the scope the person still has, or worked out again now, in the scope they have now
// (rule 4). No AI anywhere here: Ask the data being switched off doesn't close any of it.

/** A sector, pool or program a kept query names that's no longer in the asker's MyRewards. */
export const AskStale = z.enum(['sector', 'pool', 'program']);
export type AskStale = z.infer<typeof AskStale>;

/** One entry in the history list. */
export const AskConversationSummary = z.object({
  id: z.string(),
  /** The first question, or the name the person gave it. */
  title: z.string(),
  /** The person named it (Rename), so it keeps that name. */
  renamed: z.boolean(),
  /** The project it's kept in, or null when it's only in history. */
  projectId: z.string().nullable(),
  startedAt: z.string(),
  /** The last question or answer, which orders the list (newest first). */
  updatedAt: z.string(),
  /** How many answers it holds. */
  answers: z.number().int().nonnegative(),
});
export type AskConversationSummary = z.infer<typeof AskConversationSummary>;

/**
 * The history list, newest first, in groups: "Today", "Previous 7 days", then one per month
 * ("October 2026"), on the organization's calendar. `key` is `today`, `previous_7_days` or the
 * month as `YYYY-MM`; a later page may continue a group the last page ended in (same key).
 */
export const AskConversationGroup = z.object({
  key: z.string(),
  label: z.string(),
  conversations: z.array(AskConversationSummary),
});
export type AskConversationGroup = z.infer<typeof AskConversationGroup>;

export const AskConversationList = z.object({
  groups: z.array(AskConversationGroup),
  /** Pass as `before` for the next, older page; null when there's nothing older. */
  next: z.string().nullable(),
  /** The words searched for, trimmed; '' when the list isn't a search. */
  search: z.string(),
});
export type AskConversationList = z.infer<typeof AskConversationList>;

/**
 * `GET /v1/insights/ask/conversations`: `q` searches the titles and the person's own questions
 * (never the answers); `project` lists one project's conversations; `before` is the last page's
 * `next`. Other query parameters are ignored, as the app's other lists ignore them.
 */
export const AskConversationListQuery = z.object({
  q: z.string().trim().max(200).default(''),
  before: z.string().min(1).max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  project: z.uuid().optional(),
});
export type AskConversationListQuery = z.infer<typeof AskConversationListQuery>;

/** A next step of an Explore card (D12): its words, and the query a chip runs (no AI). */
export const AskExploreStep = z.object({ label: z.string(), query: AskDataQuery });
export type AskExploreStep = z.infer<typeof AskExploreStep>;

/**
 * Where a kept answer's figures come from when it's shown again: `kept`, its snapshot, taken under
 * the scope the person still has; `fresh`, worked out again now in their scope as it is now (the
 * scope changed since, the snapshot predates slice 3, or it's a Refresh); `none`, a reply with no
 * figures, or one whose words can't be shown under the scope the person has now.
 */
export const AskKeptFigures = z.enum(['kept', 'fresh', 'none']);
export type AskKeptFigures = z.infer<typeof AskKeptFigures>;

/** An answer in a kept conversation, as reopened or refreshed. */
export const AskKeptAnswer = z.object({
  messageId: z.string(),
  at: z.string(),
  /** The reply bubble: as it was given (kept), or the fresh run's words. */
  reply: z.array(z.string()),
  /** What a follow-up changed from the answer before it; empty when worked out again. */
  changes: z.array(z.object({ label: z.string(), from: z.string(), to: z.string() })),
  result: AskDataResult.nullable(),
  refusal: AskDataRefusal.nullable(),
  /** The picture the person chose for it (View as, APP-256), else the one it was answered in. */
  view: AskView,
  figures: AskKeptFigures,
  /** "As of" for the figures: the snapshot's data date, or now's; null with no figures. */
  asOf: z.string().nullable(),
  /** Why it was worked out again ("Worked out again with what you can see now."), or null. */
  note: z.string().nullable(),
  /** Its query names something no longer in the asker's MyRewards: the designed refusal shows. */
  stale: AskStale.nullable(),
  /** Worked out by AI from the question (false for words to a view, Explore and Refresh). */
  aiGenerated: z.boolean(),
  viaWords: z.literal(true).optional(),
  /** An answer an Explore card started: the card's next steps, as chips. Empty otherwise. */
  nextSteps: z.array(AskExploreStep),
  feedback: z.enum(['up', 'down']).nullable(),
  /**
   * APP-263: its Automated summary, shown again only with its snapshot (`figures: 'kept'`); an
   * answer worked out again has none (no model is asked on reopening). Absent when it had none.
   */
  automatedSummary: AskAutomatedSummary.nullable().optional(),
  /** APP-263 (D13): it was opened from a Teams or email answer. Absent otherwise. */
  openedFrom: z.enum(['teams', 'email']).optional(),
});
export type AskKeptAnswer = z.infer<typeof AskKeptAnswer>;

export const AskKeptMessage = z.discriminatedUnion('role', [
  z.object({
    role: z.literal('person'),
    messageId: z.string(),
    at: z.string(),
    /** The person's own words. */
    text: z.string(),
  }),
  AskKeptAnswer.extend({ role: z.literal('assistant') }),
]);
export type AskKeptMessage = z.infer<typeof AskKeptMessage>;

/** `GET /v1/insights/ask/conversations/:id`: the conversation, oldest message first. */
export const AskConversation = AskConversationSummary.extend({
  messages: z.array(AskKeptMessage),
  /** Earlier messages aren't shown: a conversation opens with its newest 100. */
  earlier: z.boolean(),
});
export type AskConversation = z.infer<typeof AskConversation>;

/** Rename it, or move it into or out of a project (null: back to history only). */
export const AskConversationChange = z
  .object({
    title: z.string().trim().min(1).max(120).optional(),
    projectId: z.uuid().nullable().optional(),
  })
  .strict()
  .refine((c) => c.title !== undefined || c.projectId !== undefined, {
    message: 'Change the name or the project',
  });
export type AskConversationChange = z.infer<typeof AskConversationChange>;

/** Deleted: its questions, answers and snapshots purged; the row and an audit entry stay. */
export const AskConversationDeleted = z.object({
  conversationId: z.string(),
  messages: z.number().int().nonnegative(),
});
export type AskConversationDeleted = z.infer<typeof AskConversationDeleted>;

/**
 * A project's default scope (D9): new questions in it start there, and the chips show it. Every
 * part is optional; the ids are the asker's own vocabulary's, checked when saved.
 */
export const AskProjectScope = z
  .object({
    sectorIds: z.array(Id).max(10),
    poolIds: z.array(Id).max(10),
    awardTypes: z.array(AskDataAwardType).max(5),
    period: AskDataPeriod.nullable(),
  })
  .strict();
export type AskProjectScope = z.infer<typeof AskProjectScope>;

export const AskProject = z.object({
  id: z.string(),
  name: z.string(),
  note: z.string().nullable(),
  defaultScope: AskProjectScope.nullable(),
  /** The default scope in words ("Maritime Airborne · FY 2026"); null when it has none. */
  scopeWords: z.string().nullable(),
  /** The default scope names something no longer in the asker's MyRewards. */
  stale: AskStale.nullable(),
  /** The project saved questions moved into when projects arrived ("My reports"). */
  myReports: z.boolean(),
  conversations: z.number().int().nonnegative(),
  pins: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AskProject = z.infer<typeof AskProject>;

export const AskProjectList = z.object({ projects: z.array(AskProject) });
export type AskProjectList = z.infer<typeof AskProjectList>;

const ProjectName = z.string().trim().min(1).max(80);
const ProjectNote = z.string().trim().max(500);

export const AskProjectCreate = z
  .object({
    name: ProjectName,
    /** '' is no note. */
    note: ProjectNote.nullable().default(null),
    defaultScope: AskProjectScope.nullable().default(null),
  })
  .strict();
export type AskProjectCreate = z.infer<typeof AskProjectCreate>;

export const AskProjectChange = z
  .object({
    name: ProjectName.optional(),
    note: ProjectNote.nullable().optional(),
    defaultScope: AskProjectScope.nullable().optional(),
  })
  .strict()
  .refine((c) => c.name !== undefined || c.note !== undefined || c.defaultScope !== undefined, {
    message: 'Change the name, the note or the default scope',
  });
export type AskProjectChange = z.infer<typeof AskProjectChange>;

/** Deleted: its conversations back in history, its pins removed (their text purged). */
export const AskProjectDeleted = z.object({
  projectId: z.string(),
  conversations: z.number().int().nonnegative(),
  pins: z.number().int().nonnegative(),
});
export type AskProjectDeleted = z.infer<typeof AskProjectDeleted>;

/** A pinned answer on a project's board, re-run on open in the asker's scope, with no AI. */
export const AskBoardCard = z.object({
  pinId: z.string(),
  title: z.string(),
  /** Its place on the board, from 0. */
  position: z.number().int().nonnegative(),
  /** The query it re-runs; `query.view` is the picture it was pinned in. */
  query: AskDataQuery,
  /** The query in a line, as Saved questions say it. */
  description: z.string(),
  savedAt: z.string(),
  /** The conversation it was pinned from, while that's kept; null otherwise. */
  conversationId: z.string().nullable(),
  /** It was pinned from a conversation since deleted: "That conversation was deleted". */
  conversationDeleted: z.boolean(),
  stale: AskStale.nullable(),
  /** Its answer now; null with the refusal when it can't be answered (a stale name, say). */
  result: AskDataResult.nullable(),
  refusal: AskDataRefusal.nullable(),
  asOf: z.string(),
});
export type AskBoardCard = z.infer<typeof AskBoardCard>;

export const AskBoard = z.object({
  project: AskProject,
  cards: z.array(AskBoardCard),
  /** When the board was worked out: every card in one go, so they share a data date. */
  asOf: z.string(),
});
export type AskBoard = z.infer<typeof AskBoard>;

/** The board's cards in their new order: every live pin on the board, each once. */
export const AskBoardOrder = z.object({ pinIds: z.array(z.uuid()).min(1).max(50) }).strict();
export type AskBoardOrder = z.infer<typeof AskBoardOrder>;

export const AskBoardOrdered = z.object({ projectId: z.string(), pinIds: z.array(z.string()) });
export type AskBoardOrdered = z.infer<typeof AskBoardOrdered>;

/** The board as one workbook, one sheet per card. Nothing to send: the server re-runs each. */
export const AskBoardExport = z.object({}).strict();
export type AskBoardExport = z.infer<typeof AskBoardExport>;

/** Rename a pin, change the picture it opens in, or move it to another of the asker's projects. */
export const AskPinChange = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    view: AskView.optional(),
    projectId: z.uuid().optional(),
  })
  .strict()
  .refine((c) => c.title !== undefined || c.view !== undefined || c.projectId !== undefined, {
    message: 'Change the name, the picture or the project',
  });
export type AskPinChange = z.infer<typeof AskPinChange>;

/** The Explore cards (D12): short journeys, each started without AI. */
export const AskExploreId = z.enum([
  'year_in_rewards',
  'budget_health',
  'recognition_fairness',
  'three_year_trend',
  'award_mix',
  'spot_programme',
  'head_to_head',
]);
export type AskExploreId = z.infer<typeof AskExploreId>;

export const AskExploreCard = z.object({
  id: AskExploreId,
  title: z.string(),
  description: z.string(),
  /** The first question, as the conversation keeps it. */
  question: z.string(),
  nextSteps: z.array(AskExploreStep),
});
export type AskExploreCard = z.infer<typeof AskExploreCard>;

export const AskExploreCards = z.object({ cards: z.array(AskExploreCard) });
export type AskExploreCards = z.infer<typeof AskExploreCards>;

/** Start a card: a new conversation, in one of the asker's projects if named. */
export const AskExploreStart = z.object({ projectId: z.uuid().nullable().default(null) }).strict();
export type AskExploreStart = z.infer<typeof AskExploreStart>;

/** The card's first answer, kept in its new conversation, with its next steps. */
export const AskExploreStarted = AskDataAnswer.extend({ nextSteps: z.array(AskExploreStep) });
export type AskExploreStarted = z.infer<typeof AskExploreStarted>;

/**
 * Settings › System administration › AI (D8, as APP-170 for memory): what each person has kept,
 * as counts only, never a title, a question or an answer.
 */
export const AskHistoryPerson = z.object({
  personId: z.string(),
  name: z.string(),
  /** They've left: their history is purged 30 days after (the leaver rule). */
  left: z.boolean(),
  conversations: z.number().int().nonnegative(),
  answers: z.number().int().nonnegative(),
  projects: z.number().int().nonnegative(),
  pins: z.number().int().nonnegative(),
});
export type AskHistoryPerson = z.infer<typeof AskHistoryPerson>;

export const AskHistoryCounts = z.object({
  /** Ask the data history is kept this many months unless deleted (`ai.askDataHistoryMonths`). */
  monthsKept: z.number().int().positive(),
  /** People with anything kept, by name. */
  people: z.array(AskHistoryPerson),
});
export type AskHistoryCounts = z.infer<typeof AskHistoryCounts>;

/** A System Admin purges one person's Ask the data history for them, on request, saying why. */
export const AskHistoryPurge = z.object({ reason: z.string().trim().min(1).max(2000) }).strict();
export type AskHistoryPurge = z.infer<typeof AskHistoryPurge>;

/** What was purged. Afterwards the person has nothing kept. */
export const AskHistoryPurged = z.object({
  personId: z.string(),
  conversations: z.number().int().nonnegative(),
  answers: z.number().int().nonnegative(),
  projects: z.number().int().nonnegative(),
  pins: z.number().int().nonnegative(),
});
export type AskHistoryPurged = z.infer<typeof AskHistoryPurged>;
